import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldX, PackageCheck, ArrowRightLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
  pending: "bg-amber-50 text-amber-700 border-amber-300",
  draft: "bg-slate-50 text-slate-600 border-slate-300",
  rejected: "bg-rose-50 text-rose-700 border-rose-300",
  cancelled: "bg-rose-50 text-rose-700 border-rose-300",
};

export default function VoucherVerifyPage() {
  const { vtype, vid, sig } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/voucher-verify/${vtype}/${vid}/${sig}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This document could not be verified. The QR code may be invalid or the voucher no longer exists."))
      .finally(() => setLoading(false));
  }, [vtype, vid, sig]);

  const tone = STATUS_TONE[(data?.status || "").toLowerCase()] || STATUS_TONE.draft;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center px-4 py-8" data-testid="voucher-verify-page">
      <div className="w-full max-w-2xl">
        <div className="bg-[#0B1F3A] text-white px-5 py-4 flex items-center gap-3">
          <ShieldCheck size={22} className="text-amber-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">PrimeWork · Document Verification</div>
            <div className="text-sm font-bold">Read-only voucher check — no login required</div>
          </div>
        </div>

        {loading && <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500">Verifying…</div>}

        {!loading && error && (
          <div className="bg-white border border-rose-200 p-8 text-center" data-testid="verify-error">
            <ShieldX size={40} className="text-rose-500 mx-auto mb-3" />
            <div className="font-bold text-rose-700 mb-1">Verification Failed</div>
            <div className="text-sm text-slate-600">{error}</div>
          </div>
        )}

        {!loading && data && (
          <div className="bg-white border border-slate-200" data-testid="verify-result">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{data.voucher_type_label}</div>
                <div className="text-lg font-black text-slate-900" data-testid="verify-voucher-number">{data.voucher_number}</div>
              </div>
              <span className={`px-3 py-1.5 border text-xs font-black uppercase tracking-wider ${tone}`} data-testid="verify-status">
                {data.status || "—"}
              </span>
            </div>

            <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3 border-b border-slate-100 text-sm">
              <Info label="Date" value={data.voucher_date} />
              <Info label="Branch" value={data.branch_name} />
              {data.voucher_type === "wt" ? (
                <Info label="Movement" value={`${data.from_warehouse_name} → ${data.to_warehouse_name}`} icon={<ArrowRightLeft size={12} />} />
              ) : data.voucher_type === "po" ? (
                <Info label="Vendor" value={data.vendor_name} />
              ) : (
                <Info label="Issued To" value={data.issued_to} />
              )}
              {data.project_name && <Info label="Project" value={data.project_name} />}
            </div>

            <div className="px-5 py-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1">
                <PackageCheck size={12} /> Items & Quantities ({data.line_count})
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-2">Item</th>
                    <th className="py-2 pr-2">Code</th>
                    <th className="py-2 pr-2">Type</th>
                    <th className="py-2 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody data-testid="verify-items">
                  {data.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2 pr-2 text-slate-400">{i + 1}</td>
                      <td className="py-2 pr-2 font-semibold text-slate-800">{li.name || li.batch_code}</td>
                      <td className="py-2 pr-2 text-xs text-slate-500">{li.code || li.batch_code || "—"}</td>
                      <td className="py-2 pr-2 text-xs text-slate-500">{li.line_type}</td>
                      <td className="py-2 text-right font-bold tabular-nums">{li.quantity} {li.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500">
              This page is <b>read-only</b> and shows no financial values. Check that the paper voucher's
              number, date, items and quantities match what is shown above.
              {data.scan_recorded && <span className="block mt-1 text-emerald-700">✓ This scan has been recorded as delivery proof.</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="font-semibold text-slate-800 text-sm">{value || "—"}</div>
    </div>
  );
}
